import { PaymentMethodApi } from "@app/services/apiServices/PaymentMethodApi";
import { PaymentMethod } from "@app/models/PaymentMethod";
import { UtilService } from "@app/services/Util.service";
import { SpinnerService } from "@app/services/spinner.service";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { PaymentScheduleApi } from "@app/services/apiServices/PaymentScheduleApi";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { MatDialog } from "@angular/material/dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import {
    CheckingAccountDetailsWithSchedulesDeleteDialog
} from "@app/pages/wallet/viewcheckdetails/checkingAccountDetailsWithSchedulesDelete.dialog";
import { forkJoin } from "rxjs";
import {
    CheckingAccountDetailsDeleteDialog
} from "@app/pages/wallet/viewcheckdetails/checkingAccountDetailsDelete.dialog";
import { PushPopService } from "@app/services/push.pop.service";
import { ActivatedRoute, Router } from "@angular/router";

/**
 * ViewCheckingAccountDetailsComponent view individual checking account payment method.
 */
@Component({
    selector: 'app-view-checking-account-details',
    template: require('./viewCheckingAccountDetails.html')
})
export class ViewCheckingAccountDetailsComponent implements OnInit{
    paymentMethod: PaymentMethod;
    doneLoading: boolean;

    paymentSchedules: PaymentSchedule[];
    paymentMethodId: string;
    private navItems: any;

    constructor(@Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
                @Inject(UtilService) private utilService: UtilService,
                @Inject(MatDialog) private dialog: MatDialog,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute,
                @Inject(PushPopService) private pushPopService: PushPopService) {}

    ngOnInit() {

        this.paymentMethodId = this.route.snapshot.params.paymentMethodId;
        this.doneLoading = false;
        this.spinnerService.showSpinner();
        this.get().then(() => {
            this.spinnerService.closeSpinner();
            this.doneLoading = true;
        }, () => {
            this.spinnerService.closeSpinner();
            this.dialog.open(DialogErrorDialog, {
                data: { error: 'Variable Error message based on context/code. ' }
            });
        });

    }

    /**
     * Gets checking account details for an id.
     * @returns {PaymentMethod} returns checking account payment method
     */
    get(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.paymentMethodApi.paymentMethodsIdGet(this.paymentMethodId)
                .pipe(take(1)).subscribe({ next: resp => {
                    this.paymentMethod = resp;
                    this.paymentMethodApi.paymentMethodsIdPaymentSchedulesGet(this.paymentMethodId)
                        .pipe(take(1)).subscribe({
                            next: resp => {
                                this.paymentSchedules = resp;
                                if (this.paymentSchedules) {
                                    for (let i = 0; i < this.paymentSchedules.length; i++) {
                                        let nextFuturePayment =
                                this.utilService.findNextPayment(this.paymentSchedules[i].futurePayments);
                                        this.paymentSchedules[i].nextPaymentDate = nextFuturePayment.date;
                                        this.paymentSchedules[i].nextPaymentAmount = nextFuturePayment.total;
                                    }
                                }

                                resolve();
                            }, error: () => {
                                reject();
                            } });
                }, error: err => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.get();
                            }
                            reject();
                        });
                } });
        })
    }

    /**
     * Calls paymentMethodsIdDelete to delete a specific checking account from Payment Methods.
     */
    removeCheck() {
        if (this.paymentSchedules && this.paymentSchedules.length > 0) {
            // Ask if user wants to delete related schedules, before deleting
            let scheduleConfirmationDialog = this.dialog.open( CheckingAccountDetailsWithSchedulesDeleteDialog,{
                data: { paymentSchedules: this.paymentSchedules }
            });
            scheduleConfirmationDialog.afterClosed()
                .pipe(take(1)).subscribe({
                    next: data => {
                        if (data === 'Delete') {
                            this.spinnerService.showSpinner();
                            let observableArray = [];
                            for (let i = 0; i < this.paymentSchedules.length; i++) {
                                // delete payment schedules
                                observableArray.push(
                                    this.paymentScheduleApi.paymentSchedulesIdDelete(this.paymentSchedules[i].id)
                                        .pipe(take(1)));
                            }

                            forkJoin(observableArray).subscribe({
                                next: () => {

                                    this.resetPropertyHistory();

                                    // if successful, delete the payment method
                                    this.paymentMethodApi.paymentMethodsIdDelete(this.paymentMethodId)
                                        .pipe(take(1)).subscribe({
                                            next: () => {
                                                this.spinnerService.closeSpinner();
                                                this.router.navigateByUrl('/wallet');
                                            }, error: err => {
                                                this.networkErrorService.handleNetworkError(err, true)
                                                    .pipe(take(1)).subscribe((resp) => {
                                                        if (resp === true) {
                                                            this.removeCheck();
                                                        }
                                                    });
                                            } })

                                }, error: err => {
                                    let errMsg: string = undefined;
                                    if (err.error && err.error.message) {
                                        errMsg = err.error.message;
                                    }
                                    this.errorHandlerService.handleNetworkError(err, true, errMsg)
                                        .pipe(take(1)).subscribe();
                                } })
                        }
                    }, error: err => {
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.removeCheck();
                                }
                            });
                    } })
        } else {    // no related schedules exist
            let dialogRef = this.dialog.open(CheckingAccountDetailsDeleteDialog);
            dialogRef.afterClosed().pipe(take(1)).subscribe({ next: data => {
                if (data === 'Delete') {

                    this.spinnerService.showSpinner();
                    this.paymentMethodApi.paymentMethodsIdDelete(this.paymentMethodId)
                        .pipe(take(1)).subscribe({ next: () => {
                            this.spinnerService.closeSpinner();
                            // payment method deleted - check if wallet is empty
                            this.getPaymentMethods(this.paymentMethod.payerId).then(() => {
                                this.resetPropertyHistory();
                                this.router.navigateByUrl('/wallet');
                            });
                        }, error: err => {
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe((resp) => {
                                    if (resp === true) {
                                        this.removeCheck();
                                    }
                                })
                        } });
                }
            }, error: err => {
                this.networkErrorService.handleNetworkError(err, true)
                    .pipe(take(1)).subscribe((resp) => {
                        if (resp === true) {
                            this.removeCheck();
                        }
                    });
            } });
        }

    }

    resetPropertyHistory() {
        this.navItems = this.pushPopService.getItems();
        if (this.navItems && this.navItems[0].history && this.navItems[0].history.length > 1) {
            this.navItems[0].history = [];
            this.pushPopService.setItems(this.navItems);
        }
    }

    getPaymentMethods(payerId) {
        return new Promise<void>((resolve, reject) => {
            this.paymentMethodApi.homeownersIdPaymentMethodsGet(payerId)
                .pipe(take(1)).subscribe({ next: (resp) => {
                    if (resp && resp.length > 0) {
                    // Payment methods exist
                        resolve();
                    } else {
                    // No payment methods in the wallet
                        resolve();
                    }
                }, error: (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe(() => {
                            reject();
                        });
                } });
        })
    }

    /**
     * Cancel and return to wallet landing page.
     */
    cancel() {
        this.router.navigateByUrl('/wallet');
    }

}
